/**
 * Map service for handling map-related operations
 * This service uses Mapbox for map rendering and geocoding
 */

/**
 * Get Mapbox access token from environment variables
 */
export const getMapboxToken = () => {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  
  // Disable Mapbox telemetry to prevent CORS errors
  if (token && typeof window !== 'undefined') {
    // Disable telemetry globally
    window.MAPBOX_DISABLE_TELEMETRY = true;
  }
  
  return token;
};

/**
 * Geocode a place name to coordinates
 * @param {string} placeName - The place name to geocode
 * @returns {Promise<Object>} - The geocoded result
 */
export const geocodePlace = async (placeName) => {
  try {
    const token = getMapboxToken();
    if (!token) {
      throw new Error('Mapbox token is missing. Please check your environment variables.');
    }
    
    const encodedPlaceName = encodeURIComponent(placeName);
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedPlaceName}.json?access_token=${token}&limit=5`
    );
    
    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.features.map(feature => ({
      id: feature.id,
      name: feature.place_name,
      lat: feature.center[1],
      lng: feature.center[0],
      placeType: feature.place_type[0],
      properties: feature.properties
    }));
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
};

/**
 * Reverse geocode coordinates to place name
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object>} - The reverse geocoded result
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const token = getMapboxToken();
    if (!token) {
      throw new Error('Mapbox token is missing. Please check your environment variables.');
    }
    
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}`
    );
    
    if (!response.ok) {
      throw new Error(`Reverse geocoding failed: ${response.statusText}`);
    }
    
    const data = await response.json();
    if (data.features.length === 0) {
      throw new Error('No results found for the provided coordinates');
    }
    
    return {
      name: data.features[0].place_name,
      lat,
      lng,
      placeType: data.features[0].place_type[0],
      properties: data.features[0].properties
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};

/**
 * Calculate the bounds to encompass multiple coordinates
 * @param {Array<Object>} coordinates - Array of {lat, lng} objects
 * @returns {Object} - The bounds object
 */
export const calculateBounds = (coordinates) => {
  if (!coordinates || coordinates.length === 0) {
    // Default to world bounds if no coordinates provided
    return {
      sw: { lat: -90, lng: -180 },
      ne: { lat: 90, lng: 180 }
    };
  }
  
  // If only one coordinate, create a small box around it
  if (coordinates.length === 1) {
    const { lat, lng } = coordinates[0];
    return {
      sw: { lat: lat - 0.01, lng: lng - 0.01 },
      ne: { lat: lat + 0.01, lng: lng + 0.01 }
    };
  }
  
  // Calculate bounds for multiple coordinates
  let minLat = coordinates[0].lat;
  let maxLat = coordinates[0].lat;
  let minLng = coordinates[0].lng;
  let maxLng = coordinates[0].lng;
  
  coordinates.forEach(coord => {
    minLat = Math.min(minLat, coord.lat);
    maxLat = Math.max(maxLat, coord.lat);
    minLng = Math.min(minLng, coord.lng);
    maxLng = Math.max(maxLng, coord.lng);
  });
  
  // Add a small padding (5%)
  const latPadding = (maxLat - minLat) * 0.05;
  const lngPadding = (maxLng - minLng) * 0.05;
  
  return {
    sw: { lat: minLat - latPadding, lng: minLng - lngPadding },
    ne: { lat: maxLat + latPadding, lng: maxLng + lngPadding }
  };
}; 

/**
 * Comprehensive forward search using Mapbox Geocoding.
 * Returns raw Mapbox features so callers can use place_name/center etc.
 * @param {string} query - The search text
 * @param {Object} options - Optional params { proximity: 'lng,lat', limit: number, language: string }
 * @returns {Promise<Array>} - Mapbox features array
 */
export const comprehensiveSearch = async (query, options = {}) => {
  try {
    const token = getMapboxToken();
    if (!token) {
      throw new Error('Mapbox token is missing. Please check your environment variables.');
    }

    const encoded = encodeURIComponent(query);
    const params = new URLSearchParams();
    params.set('access_token', token);
    params.set('limit', String(options.limit || 5));
    if (options.proximity) params.set('proximity', options.proximity);
    if (options.language) params.set('language', options.language);

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?${params.toString()}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Search failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data.features) ? data.features : [];
  } catch (error) {
    console.error('Comprehensive search error:', error);
    throw error;
  }
};